// What counts as an engine fault worth telling an owner about.
//
// Two different counts arrive from the live payload and only one of them means
// "fault":
//   dtc_count        PENDING - the car saw something once and is not yet
//                    convinced. Never gate a warning on this.
//   stored_dtc_count CONFIRMED - what the check-engine lamp is reporting.
//
// Gating on the pending count had the badge and the lamp measuring different
// things. On prod every asset reads pending 0 while two of them carry 1 and 7
// stored codes, so a car with seven confirmed faults showed no icon at all,
// while a car with two unconfirmed ones showed a warning it had not earned.
//
// Deliberately free of imports so it is unit-testable on its own; useObdStatus
// wraps it with the vehicle/phone gating.

/** The check-engine lamp, read truthily. */
export function milOn(device) {
    // Truthy, not `=== true`: the REST load sends a real bool but the live MQTT
    // obd_update path stores 1/0, so a strict compare would drop the warning
    // exactly when the lamp turns on mid-session.
    return !!device?.indication_lamp_status;
}

/** Confirmed (stored) fault count. */
export function confirmedFaultCount(device) {
    return Number(device?.stored_dtc_count ?? 0) || 0;
}

/** Pending count. Readable, but never a reason to warn. */
export function pendingCodeCount(device) {
    return Number(device?.dtc_count ?? 0) || 0;
}

/** Whether there is anything to warn the owner about at all. */
export function hasConfirmedFault(device) {
    return milOn(device) || confirmedFaultCount(device) > 0;
}

/** Short label for the check-engine affordance. */
export function faultHint(device) {
    // "fault", not "DTC": the person reading this owns a car, not a scan tool.
    const n = confirmedFaultCount(device);
    const text = n > 0 ? `${n} fault${n === 1 ? "" : "s"}` : "";
    if (milOn(device) && text) return `Check engine light on - ${text}`;
    if (milOn(device)) return "Check engine light on";
    return text || "Check engine";
}
